<template>
  <sc-flex :is-column="true">
    <input
      type="file"
      style="display: none"
      ref="uploadVoucherInput"
      multiple
      accept=".pdf"
      @change="handleUploadVoucher"
    />

    <WarningDialog
      :opened="unreviewedVouchersWarningDialogOpened"
      :allow-skip="true"
      warning-title="Some pages are not reviewed"
      warning-text='There are some pages that you did not review. Please press "Close" to review the highlighted pages in red or press "Skip" to proceed without reviewing.'
      @close="unreviewedVouchersWarningDialogOpened = false"
      @skip="finishReviewing(true)"
    />

    <WarningDialog
      :opened="erroredVouchersWarningDialogOpened"
      :allow-skip="false"
      warning-title="Some pages need to be corrected"
      warning-text='There are some fields that are invalid or empty. Please press "Close" to review the highlighted pages below in red.'
      @close="erroredVouchersWarningDialogOpened = false"
    />

    <!-- Partial: Alerts -->
    <sc-alert v-if="taxReturn.status == 'action_required'" type="info">
      <span v-if="taxReturn.payment_vouchers.length === 0">
        <b>Instruction:</b> You can add tax payments here or go to the next
        section.
      </span>

      <span v-else>
        <b>Instruction:</b> Please review all payments and adjust payment
        details as necessary.
      </span>

      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          @click="finishReviewing()"
          :is-disabled="saving || pdfLoading"
          icon-right="arrow-right"
        >
          Finished Reviewing
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <sc-alert type="info" v-else>
      <span
        ><b>Note:</b> Payment vouchers cannot be edited once they're marked as
        <b>PAID</b>.</span
      >

      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          icon-left="arrow-right"
          @click="next"
          :is-loading="saving"
          :is-disabled="pdfLoading"
        >
          Next
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <sc-flex>
      <!-- Partial: Sidebar -->
      <sc-flex class="sidebar" :is-column="true" :gap="8">
        <v-layout align-center justify-space-between>
          <sc-h5>
            Payments
          </sc-h5>

          <v-btn
            small
            class="text-none"
            color="primary"
            @click="addPayment"
            :disabled="taxReturn.status === 'completed' || saving"
          >
            <v-icon left small class="mr-1">add</v-icon>

            <span>Add</span>
          </v-btn>
        </v-layout>

        <ul class="page-list">
          <li
            v-for="(voucher, index) in taxReturn.payment_vouchers"
            :key="voucher.page"
            :class="{
              selected: currentPaymentVoucherIndex === index + 1,
              unreviewed:
                shouldHighlightUnreviewedVouchers && !voucher.reviewed_by,
              errored:
                shouldHighlightErroredVouchers && isVoucherInvalid(voucher),
            }"
          >
            <a @click="currentPaymentVoucherIndex = index + 1">
              <v-layout>
                <v-icon class="mr-1">
                  {{
                    shouldHighlightErroredVouchers && isVoucherInvalid(voucher)
                      ? 'error'
                      : 'bookmark_border'
                  }}
                </v-icon>

                <span>
                  {{ getFullPaymentName(voucher) }}

                  <span
                    v-if="voucher.status === 'analyzing'"
                    class="grey--text body-1"
                  >
                    (analyzing...)
                  </span>
                </span>
              </v-layout>

              <v-layout class="ml-4">
                <v-chip
                  v-if="voucher.is_paid"
                  label
                  small
                  color="success"
                  style="border-radius: 5px; color: #fff"
                >
                  Paid
                </v-chip>

                <v-chip
                  v-if="voucher.is_archived"
                  label
                  small
                  color="grey"
                  style="border-radius: 5px; color: #fff"
                >
                  Archived
                </v-chip>
              </v-layout>
            </a>
          </li>
        </ul>
      </sc-flex>

      <div class="pdf-container">
        <!-- Partial: Toolbar -->
        <v-layout class="toolbar">
          <sc-h4>
            <span v-if="currentPaymentVoucher">
              {{ getFullPaymentName(currentPaymentVoucher) }}

              <span
                v-if="currentPaymentVoucher.status === 'analyzing'"
                class="grey--text body-1"
              >
                (analyzing...)
              </span>
            </span>
          </sc-h4>

          <sc-button
            v-if="
              taxReturn.filing_instructions &&
                taxReturn.filing_instructions.length > 0
            "
            type="primary"
            size="small"
            @click="$emit('open-filing-instructions')"
          >
            View Filing Instructions
          </sc-button>
        </v-layout>

        <v-layout
          class="pdf-container"
          justify-center
          align-center
          v-if="!currentPaymentVoucher"
        >
          <span>
            You can add tax payments here or go to the next section.
          </span>
        </v-layout>

        <!-- Partial: Content -->
        <v-layout class="page-container" v-else>
          <v-layout
            id="payment-voucher-scrollable-container"
            class="scrollable-container"
          >
            <v-layout class="pdf-page-container">
              <pdf
                v-if="currentPaymentVoucher.document_location"
                @page-loaded="currentPaymentVoucherPdfDataLoaded = true"
                :src="currentPaymentVoucherPdfData"
                class="pdf ph-no-capture"
              />

              <v-layout v-else-if="!pdfPageIndex" class="pa-4" column>
                <span>
                  This payment does not have an associated voucher. You still
                  can fill out the payment information on the right sidebar for
                  the client to make the necessary payment. Optionally, you can
                  upload an associated voucher below.
                </span>

                <v-layout align-center>
                  <v-btn
                    color="primary"
                    class="mx-0 my-3 text-none"
                    @click="selectFileToUploadVoucher"
                    :loading="uploadingPaymentVoucher"
                  >
                    <v-icon class="material-icons-outlined" left>
                      file_upload
                    </v-icon>
                    Upload an associated voucher
                  </v-btn>

                  <div class="ml-3">
                    <i>* Only a single-page PDF is supported.</i>
                  </div>
                </v-layout>
              </v-layout>

              <v-layout v-else-if="pdfLoading" justify-center align-center>
                <v-progress-circular
                  class="mt-4"
                  indeterminate
                  color="primary"
                />
              </v-layout>

              <pdf
                v-else
                @page-loaded="pdfLoaded = true"
                @progress="pdfProgress = $event"
                :page="pdfPageIndex"
                :src="pdfData"
                class="pdf ph-no-capture"
              />
            </v-layout>
          </v-layout>

          <sc-flex
            :is-column="true"
            :gap="16"
            v-if="currentPaymentVoucher"
            class="sidebar--right"
          >
            <v-form ref="form">
              <sc-h3>Payment Details</sc-h3>

              <v-alert
                v-if="currentPaymentVoucher.is_paid"
                :value="currentPaymentVoucher.is_paid"
                color="success"
                class="mt-3 text-xs-left"
                :style="'border-radius: 5px;'"
              >
                This voucher is marked as <b>PAID</b>, therefore it cannot be
                edited.
              </v-alert>

              <v-alert
                v-else-if="currentPaymentVoucher.is_archived"
                :value="currentPaymentVoucher.is_archived"
                color="darkgrey"
                class="mt-3 text-xs-left"
                :style="'border-radius: 5px;'"
              >
                This voucher is <b>ARCHIVED</b>, therefore it cannot be edited.
              </v-alert>

              <sc-flex :is-column="true" :gap="8">
                <sc-flex :is-column="true" :gap="8" class="form-control">
                  <sc-h5
                    >Payment type: <span class="error--text">*</span></sc-h5
                  >
                  <v-autocomplete
                    solo
                    hide-details
                    class="mt-1 select-input"
                    placeholder="Select payment type"
                    v-model="currentPaymentVoucher.voucher_id"
                    :items="selectableVoucherType"
                    :item-text="item => item.name + ' (' + item.code + ')'"
                    :item-value="item => item.id"
                    :disabled="!isCurrentPaymentEditable"
                    :rules="
                      currentPaymentVoucher.pay_by_check_disabled
                        ? [rules.required]
                        : undefined
                    "
                    @change="updatePaymentLink"
                  />
                </sc-flex>

                <sc-flex
                  :is-column="true"
                  :gap="8"
                  class="form-control"
                  v-if="currentPaymentVoucher.voucher_id === 'other'"
                >
                  <sc-h5>Payment name: </sc-h5>
                  <v-combobox
                    solo
                    hide-details
                    clearable
                    class="mt-1 select-input"
                    :items="customPaymentNames"
                    item-text="name"
                    item-value="name"
                    :value="currentPaymentVoucher.custom_payment_name"
                    :append-icon="null"
                    :disabled="!isCurrentPaymentEditable"
                    :rules="[rules.required]"
                    @change="addCustomPaymentName($event)"
                  >
                    <v-layout
                      slot="item"
                      slot-scope="props"
                      align-center
                      justify-space-between
                    >
                      <span>
                        {{ props.item.name }}
                      </span>

                      <v-btn
                        icon
                        ripple
                        @click.prevent.stop="
                          deleteCustomPaymentName(props.item)
                        "
                      >
                        <v-icon
                          color="error lighten-1"
                          class="material-icons-outlined"
                          >delete</v-icon
                        >
                      </v-btn>
                    </v-layout>
                  </v-combobox>
                </sc-flex>

                <sc-flex :is-column="true">
                  <sc-flex :is-column="true" :gap="8" class="form-control">
                    <sc-h5>Quarter:</sc-h5>

                    <v-select
                      solo
                      hide-details
                      class="mt-1 select-input"
                      :items="['None', 'Q1', 'Q2', 'Q3', 'Q4']"
                      placeholder="None"
                      v-model="currentPaymentVoucher.quarter"
                      :disabled="!isCurrentPaymentEditable"
                    />
                  </sc-flex>
                </sc-flex>

                <sc-flex :is-column="true">
                  <sc-flex :is-column="true" :gap="8" class="form-control">
                    <sc-h5>Due date: <span class="error--text">*</span></sc-h5>
                    <v-text-field
                      v-if="!currentPaymentVoucher.is_due_asap"
                      :key="currentPaymentVoucherIndex"
                      solo
                      hide-details
                      class="mt-1 select-input"
                      placeholder="Select due date"
                      v-model="currentPaymentVoucher.due_date"
                      type="date"
                      :disabled="!isCurrentPaymentEditable"
                      :error="isEmpty(currentPaymentVoucher.due_date)"
                    />

                    <v-text-field
                      v-else
                      solo
                      hide-details
                      class="mt-1 select-input"
                      :disabled="true"
                      value="As soon as possible"
                    />
                  </sc-flex>
                  <sc-flex :is-column="true">
                    <v-checkbox
                      hide-details
                      v-model="currentPaymentVoucher.is_due_asap"
                      :disabled="!isCurrentPaymentEditable"
                      label="Due ASAP"
                    ></v-checkbox>
                  </sc-flex>
                </sc-flex>

                <sc-flex :is-column="true" :gap="8" class="form-control">
                  <sc-h5>Amount: <span class="error--text">*</span></sc-h5>
                  <CurrencyInput
                    v-model="currentPaymentVoucher.amount"
                    :options="{ currency: 'USD' }"
                    :disabled="!isCurrentPaymentEditable"
                    :class="{
                      'error--text': currentPaymentVoucher.amount === 0,
                    }"
                  />

                  <sc-flex :is-column="true">
                    <v-checkbox
                      hide-details
                      v-model="currentPaymentVoucher.pay_by_check_disabled"
                      :disabled="
                        !isCurrentPaymentEditable ||
                          isPayByCheckCheckboxDisabled
                      "
                      label="Disable Pay by Check"
                    ></v-checkbox>
                  </sc-flex>
                </sc-flex>
                <sc-flex :is-column="true" :gap="8" class="form-control">
                  <sc-h5
                    >Payment link:
                    <span
                      v-if="currentPaymentVoucher.pay_by_check_disabled"
                      class="error--text"
                      >*</span
                    >
                  </sc-h5>
                  <v-text-field
                    solo
                    hide-details
                    placeholder="https://"
                    v-model="currentPaymentVoucher.payment_link"
                    class="mt-0 mb-0 select-input"
                    :disabled="!isCurrentPaymentEditable"
                    :rules="[rules.required]"
                    @blur="onBlurOfPaymentLink"
                  />
                </sc-flex>

                <sc-flex :is-column="true" :gap="8" class="form-control">
                  <sc-h5>Payment instruction link:</sc-h5>
                  <v-text-field
                    solo
                    hide-details
                    placeholder="https://"
                    v-model="currentPaymentVoucher.payment_instruction_link"
                    class="mt-0 mb-0 select-input"
                    :disabled="!isCurrentPaymentEditable"
                    @blur="onBlurOfPaymentInstructionLink"
                  />
                </sc-flex>

                <v-divider class="mt-4" />

                <v-layout class="mt-2" column>
                  <v-layout>
                    <v-btn
                      v-if="!currentPaymentVoucher.is_archived"
                      color="darkgrey"
                      class="text-none"
                      block
                      flat
                      @click="archivePayment(currentPaymentVoucherIndex - 1)"
                      :disabled="currentPaymentVoucher.is_paid"
                    >
                      <v-icon left class="material-icons-outlined"
                        >inventory_2</v-icon
                      >
                      Archive this payment
                    </v-btn>

                    <v-btn
                      v-else
                      color="darkgrey"
                      class="text-none"
                      block
                      flat
                      @click="unarchivePayment(currentPaymentVoucherIndex - 1)"
                      :disabled="currentPaymentVoucher.is_paid"
                    >
                      <v-icon left class="material-icons-outlined"
                        >restore</v-icon
                      >
                      Unarchive this payment
                    </v-btn>
                  </v-layout>

                  <v-layout>
                    <v-btn
                      color="error"
                      class="text-none"
                      block
                      flat
                      @click="removePayment"
                      :disabled="!isCurrentPaymentEditable"
                    >
                      <v-icon left class="material-icons-outlined"
                        >delete</v-icon
                      >
                      Remove this payment
                    </v-btn>
                  </v-layout>
                </v-layout>
              </sc-flex>
            </v-form>
          </sc-flex>
        </v-layout>
      </div>
    </sc-flex>
  </sc-flex>
</template>

<script>
import pdf from 'vue-pdf';
import API from '@/api';
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';
import axios from 'axios';
import { Sema } from 'async-sema';
import { PDFDocument } from 'pdf-lib';
import ReadonlyAlert from '@/components/returns/ReadonlyAlert';
import CurrencyInput from '@/components/returns/CurrencyInput';
import WarningDialog from '@/components/returns/WarningDialog';
import chunk from 'lodash/chunk';

export default {
  components: {
    pdf,
    ReadonlyAlert,
    CurrencyInput,
    WarningDialog,
  },

  props: {
    currentCompany: {
      type: Object,
      default: () => {},
    },

    taxReturn: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      currentPaymentVoucherIndex: null,

      pdfProgress: 0,
      pdfLoaded: false,
      pdfData: null,
      pdfDoc: null,
      pdfLoading: false,
      pdfPageIndex: null,

      saving: false,

      // States for forms with errors
      erroredVouchersWarningDialogOpened: false,
      shouldHighlightErroredVouchers: false,

      // States for unreviewed forms
      unreviewedVouchersWarningDialogOpened: false,
      shouldHighlightUnreviewedVouchers: false,

      selectableVoucherType: [],
      customPaymentNames: [],

      rules: {
        required: value => !!value || 'Required.',
      },

      uploadVoucherInput: null,
      uploadingPaymentVoucher: false,
      currentPaymentVoucherPdfData: null,
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentUserFirm: 'currentFirm',
    }),

    currentPaymentVoucher() {
      if (this.currentPaymentVoucherIndex === null) {
        return null;
      } else {
        return this.taxReturn.payment_vouchers[
          this.currentPaymentVoucherIndex - 1
        ];
      }
    },

    isCurrentPaymentEditable() {
      return (
        this.taxReturn.status !== 'completed' &&
        !this.currentPaymentVoucher.is_paid &&
        !this.currentPaymentVoucher.is_archived
      );
    },

    // Disabled when the voucher does not have a page or does not have an associated document
    isPayByCheckCheckboxDisabled() {
      return (
        this.currentPaymentVoucher.page === null &&
        !this.currentPaymentVoucher.document_location
      );
    },
  },

  methods: {
    isEmpty,

    updateCurrentPaymentVoucherIndex($event) {
      let newIndex = parseInt($event.target.value);

      // Make sure the index is not out of bounds
      if (newIndex < 1) {
        newIndex = 1;
      } else if (newIndex > this.taxReturn.payment_vouchers.length) {
        newIndex = this.taxReturn.payment_vouchers.length;
      }

      this.currentPaymentVoucherIndex = newIndex;
      $event.target.value = newIndex;
    },

    async save() {
      const response = await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        ...this.taxReturn,
        payment_vouchers: this.taxReturn.payment_vouchers,
      });

      return response.data;
    },

    async finishReviewing(skipUnreviewed = false) {
      if (this.hasErrors()) {
        this.erroredVouchersWarningDialogOpened = true;
        this.shouldHighlightErroredVouchers = true;
        this.validateInputs();
        return;
      }

      if (!skipUnreviewed && this.hasUnreviewedVouchers()) {
        this.unreviewedVouchersWarningDialogOpened = true;
        this.shouldHighlightUnreviewedVouchers = true;
        return;
      }

      const taxReturn = await this.save();

      this.$emit('payment-vouchers-updated', taxReturn);
    },

    getVoucherDefinitionById(id) {
      return this.selectableVoucherType.find(voucher => voucher.id === id);
    },

    async getVoucherDefinitions() {
      const response = await axios.get(`/tax_returns/get_voucher_definitions`);
      this.selectableVoucherType = response.data;
    },

    async getVoucherInfo(page) {
      const response = await axios.post(
        `/tax_returns/${this.taxReturn.id}/analyze_payment_voucher`,
        {
          company_id: this.currentCompany.id,
          page,
        },
      );

      return response.data;
    },

    getFullPaymentName(paymentVoucher) {
      let paymentName =
        paymentVoucher.voucher_id === 'other'
          ? paymentVoucher.custom_payment_name
          : this.getVoucherDefinitionById(paymentVoucher.voucher_id)
          ? `${
              this.getVoucherDefinitionById(paymentVoucher.voucher_id).name
            } (${paymentVoucher.voucher_id})`
          : 'Unknown';

      if (paymentVoucher.quarter && paymentVoucher.quarter !== 'None') {
        paymentName += ` - ${paymentVoucher.quarter}`;
      }

      return paymentName;
    },

    next() {
      this.$emit('next');
    },

    async analyzeAllPaymentVouchers() {
      const vouchersToAnalyze = this.taxReturn.payment_vouchers
        .filter(voucher => {
          return (
            voucher.voucher_id === 'unknown' && voucher.status !== 'analyzed'
          );
        })
        .map(voucher => {
          voucher.status = 'analyzing';
          return voucher;
        });

      const sema = new Sema(4); // Limit to 4 concurrent requests

      const analyzeVoucher = async voucher => {
        await sema.acquire();

        const pages = [voucher.page];

        try {
          const response = await axios.post(
            `/tax_returns/${this.taxReturn.id}/analyze_payment_vouchers`,
            {
              company_id: this.currentCompany.id,
              pages,
            },
          );

          response.data.forEach(result => {
            if (voucher.page === result.page) {
              try {
                const extractedInfo = result.extracted_info;
                if (extractedInfo !== 'unknown') {
                  voucher.voucher_id = extractedInfo.form_id;
                  voucher.quarter = extractedInfo.quarter;
                  voucher.due_date = extractedInfo.due_date;
                  if (isEmpty(voucher.amount)) {
                    voucher.amount = extractedInfo.due_amount;
                  }

                  const voucherDefinition = this.getVoucherDefinitionById(
                    voucher.voucher_id,
                  );

                  if (voucherDefinition) {
                    voucher.payment_link = voucherDefinition.payment_link;
                  }
                }

                voucher.status = 'analyzed';
              } catch (error) {
                voucher.status = 'analyze_failed';
              }
            }
          });
        } catch (error) {
          voucher.status = 'analyze_failed';
        } finally {
          sema.release();
        }
      };

      const analyzeTasks = vouchersToAnalyze.map(voucher =>
        analyzeVoucher(voucher),
      );

      await Promise.all(analyzeTasks);
    },

    isDueASAP(dueDate) {
      return this.currentPaymentVoucher.due_date === 'ASAP';
    },

    scrollToBottom() {
      const scrollableContainer = document.querySelector(
        '#payment-voucher-scrollable-container',
      );

      if (scrollableContainer) {
        scrollableContainer.scrollTo({
          top: scrollableContainer.scrollHeight,
          behavior: 'smooth',
        });
      }
    },

    getUnreviewedVouchers() {
      return this.taxReturn.payment_vouchers.filter(voucher => {
        return !voucher.reviewed_by;
      });
    },

    hasUnreviewedVouchers() {
      return this.getUnreviewedVouchers().length > 0;
    },

    hasErrors() {
      return this.taxReturn.payment_vouchers.some(voucher => {
        return this.isVoucherInvalid(voucher);
      });
    },

    async markCurrentPaymentVoucherReviewed() {
      this.currentPaymentVoucher.reviewed_by = this.currentUser.id;
      await this.save();
    },

    isVoucherInvalid(voucher) {
      if (voucher.voucher_id === 'unknown' || isEmpty(voucher.voucher_id)) {
        return true;
      } else if (isEmpty(voucher.due_date) && !voucher.is_due_asap) {
        return true;
      } else if (voucher.amount === 0) {
        return true;
      } else if (
        voucher.pay_by_check_disabled &&
        isEmpty(voucher.payment_link)
      ) {
        return true;
      } else {
        return false;
      }
    },

    validateInputs() {
      this.$refs.form.validate();
    },

    async getCustomPaymentNames() {
      const response = await API.firm.getCustomPaymentNames({
        firmId: this.currentUserFirm.id,
      });

      this.customPaymentNames = response.data;
    },

    async addCustomPaymentName(item) {
      // Depends on whether item is selected or typed, it can be either an object or just a string;
      const name = item && item.name ? item.name : item;

      this.currentPaymentVoucher.custom_payment_name = name;

      if (isEmpty(name)) {
        return;
      }

      const response = await API.firm.addCustomPaymentName({
        firmId: this.currentUserFirm.id,
        name,
      });

      this.customPaymentNames.push(response.data);
    },

    async deleteCustomPaymentName(item) {
      const response = await API.firm.deleteCustomPaymentName({
        firmId: this.currentUserFirm.id,
        id: item.id,
      });

      this.customPaymentNames = this.customPaymentNames.filter(
        customPaymentName => customPaymentName.id !== item.id,
      );
    },

    async addPayment() {
      const newPayment = {
        voucher_id: 'unknown',
        page: null,
        due_date: '',
        amount: 0,
        payment_link: '',
        payment_instruction_link: '',
        reviewed_by: null,
        status: 'analyzed',
        pay_by_check_disabled: false,
      };

      this.taxReturn.payment_vouchers.push(newPayment);

      this.currentPaymentVoucherIndex = this.taxReturn.payment_vouchers.length;
    },

    async removePayment() {
      this.taxReturn.payment_vouchers.splice(
        this.currentPaymentVoucherIndex - 1,
        1,
      );
      this.currentPaymentVoucherIndex = this.currentPaymentVoucherIndex - 1;
      await this.save();
    },

    async archivePayment(index) {
      this.currentPaymentVoucher.is_archived = true;
      await this.save();

      // Unsure why this is necessary, but without it, the update doesn't trigger a re-render
      this.$forceUpdate();
    },

    async unarchivePayment(index) {
      this.currentPaymentVoucher.is_archived = false;
      await this.save();

      // Unsure why this is necessary, but without it, the update doesn't trigger a re-render
      this.$forceUpdate();
    },

    async updatePaymentLink() {
      const voucherDefinition = this.getVoucherDefinitionById(
        this.currentPaymentVoucher.voucher_id,
      );

      if (voucherDefinition && voucherDefinition.payment_link) {
        this.currentPaymentVoucher.payment_link =
          voucherDefinition.payment_link;
      } else {
        this.currentPaymentVoucher.payment_link = '';
      }
    },

    selectFileToUploadVoucher() {
      this.$refs.uploadVoucherInput.click();
    },

    async handleUploadVoucher($e) {
      const currentPaymentIndex = this.currentPaymentVoucherIndex - 1;

      this.uploadingPaymentVoucher = true;

      try {
        const file = $e.target.files[0];

        // Check if this is a single-page PDF
        const pdfDoc = await PDFDocument.load(await file.arrayBuffer());
        const pdfData = await pdfDoc.saveAsBase64({ dataUri: true });

        if (pdfDoc.getPageCount() !== 1) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'Please upload a single-page pdf.',
          });

          return;
        }

        const { documentName, documentLocation } = await this.uploadVoucher(
          file,
        );

        this.$set(this.taxReturn.payment_vouchers, currentPaymentIndex, {
          ...this.taxReturn.payment_vouchers[currentPaymentIndex],
          document_location: documentLocation,
        });

        await this.save();
      } finally {
        this.uploadingPaymentVoucher = false;
      }
    },

    async uploadVoucher(file) {
      const {
        data: { id, upload_link },
      } = await API.taxReturn.getUploadLinkForPaymentVoucher({
        companyId: this.currentCompany.id,
      });

      await API.integration.uploadToDropbox(upload_link, file);

      const response = await API.taxReturn.processPaymentVoucher({
        fileId: id,
        taxReturnId: this.taxReturn.id,
        documentName: file.name,
        companyId: this.currentCompany.id,
      });

      return {
        documentName: response.data.document_name,
        documentLocation: response.data.document_location,
      };
    },

    onBlurOfPaymentLink(event) {
      const { value } = event.target;

      if (value && !value.trim().startsWith('http')) {
        this.currentPaymentVoucher.payment_link = 'https://' + value.trim();
      }
    },

    onBlurOfPaymentInstructionLink(event) {
      const { value } = event.target;

      if (value && !value.trim().startsWith('http')) {
        this.currentPaymentVoucher.payment_instruction_link =
          'https://' + value.trim();
      }
    },
  },

  watch: {
    async currentPaymentVoucherIndex() {
      if (this.currentPaymentVoucher) {
        if (this.currentPaymentVoucher.page) {
          this.pdfPageIndex = this.currentPaymentVoucher.page;
          this.scrollToBottom();
        } else {
          this.pdfPageIndex = null;
        }

        if (
          this.currentPaymentVoucher.page === null &&
          !this.currentPaymentVoucher.document_location
        ) {
          this.currentPaymentVoucher.pay_by_check_disabled = true;
        }

        await this.markCurrentPaymentVoucherReviewed();
      }
    },

    async 'currentPaymentVoucher.document_location'(newValue) {
      this.currentPaymentVoucherPdfData = null;
      this.currentPaymentVoucherPdfDataLoaded = false;

      if (newValue) {
        const response = await API.integration.downloadDocument({
          companyId: this.currentCompany.id,
          link: this.currentPaymentVoucher.document_location,
        });

        const pdfSrc = response.data.document.data.link;
        const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

        const pdfDoc = await PDFDocument.load(pdfBytes);
        this.currentPaymentVoucherPdfData = await pdfDoc.saveAsBase64({
          dataUri: true,
        });
      }
    },

    async pdfLoaded() {
      if (this.pdfLoaded) {
        this.scrollToBottom();
        await this.markCurrentPaymentVoucherReviewed();
      }
    },
  },

  async created() {
    await this.getVoucherDefinitions();
  },

  async beforeDestroy() {
    await this.save();
  },

  async mounted() {
    this.getCustomPaymentNames();

    try {
      this.pdfLoading = true;

      const response = await API.integration.downloadDocument({
        companyId: this.currentCompany.id,
        link: this.taxReturn.original_file,
      });

      const pdfSrc = response.data.document.data.link;
      const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

      this.pdfDoc = await PDFDocument.load(pdfBytes);
      this.pdfData = await this.pdfDoc.saveAsBase64({ dataUri: true });

      if (this.taxReturn.payment_vouchers.length > 0) {
        this.currentPaymentVoucherIndex = 1;

        this.pdfPageIndex = this.taxReturn.payment_vouchers[
          this.currentPaymentVoucherIndex - 1
        ].page;
      }

      this.analyzeAllPaymentVouchers();
    } finally {
      this.pdfLoading = false;
    }
  },
};
</script>

<style lang="postcss" scoped>
.sidebar {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 20px;
  max-height: calc(100dvh - 120px);
  min-height: calc(100dvh - 120px);
  min-width: 260px;
  max-width: 260px;
  overflow: auto;
  padding: 20px 20px 32px;

  background-color: var(--gray-100);
  border-right: 1px solid var(--gray-300);
}

@media (max-width: 860px) {
  .sidebar {
    display: none;
  }
}

.pdf-container {
  flex: auto;
  overflow-y: auto;
  position: relative;
  min-height: calc(100dvh - 120px);
  max-height: calc(100dvh - 120px);
}

.toolbar {
  box-sizing: border-box;
  align-items: center;
  display: flex;
  gap: 16px;
  height: 56px;
  justify-content: space-between;
  padding: 0 16px;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--gray-100);
  border-bottom: 1px solid var(--gray-200);
}

.toolbar__pageNavigation {
  display: flex;
  justify-content: center;
  min-width: calc(100% - 360px);

  & >>> sc-button[is-disabled] {
    pointer-events: none;
  }
}

.toolbar__pageActions {
  width: 360px;
}

.toolbar input {
  height: 30px;
  padding: 0;
  width: 40px;

  border: 0;
  border-radius: 2px;
  outline: 1px solid var(--gray-300);
  background-color: var(--gray-0);

  font-family: var(--font-family-sans);
  line-height: 32px;
  text-align: center;
}

.toolbar input:active,
.toolbar input:focus {
  outline: 1px solid var(--blue-400);
}

.page-container {
  flex: auto;
  width: 100%;
  height: 100%;
  min-height: calc(100dvh - 176px);
  max-height: calc(100dvh - 176px);
}

.scrollable-container {
  overflow: auto;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.15);
}

.pdf-page-container {
  position: relative;
  height: max-content;
  max-width: 1000px;
  margin: 0 auto;
  flex-direction: column;
}

.pdf {
  position: relative;
  padding: 0;
  max-width: 1000px;
  width: 100%;
}

.page-list {
  padding-left: 0px;
}

.page-list li {
  padding: 4px;
  list-style: none;
}

.page-list li a {
  color: #444;
  text-decoration: none;
}

.page-list li a i {
  color: #444;
}

.page-list li.errored a,
.page-list li.errored a i,
.page-list li.unreviewed a,
.page-list li.unreviewed a i {
  color: var(--red-700);
}

.page-list li.selected {
  background-color: rgba(0, 0, 0, 0.07);
  border-radius: 4px;
}

.sidebar--right {
  box-sizing: border-box;
  overflow-y: auto;
  max-width: 320px;
  min-width: 320px;
  padding: 16px;
  padding-bottom: 100px;

  background-color: var(--gray-100);
  border-left: 1px solid var(--gray-200);
}

.sidebar--right * {
  box-sizing: border-box !important;
}

.form-control {
  margin-top: 16px;

  & >>> .v-text-field {
    border: 1px solid #ccc;
  }

  & >>> .v-text-field .v-input__slot {
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.15) !important;
  }
}

.form-control .error--text {
  &.v-input {
    border: 1px solid var(--red-500);
  }
}
</style>
